'use client';

import {
  getEligibleCustomEventPopups,
  isGlobalPopupPathMatch,
  selectHighestPriorityPopup,
} from '@/helper/globalPopupEligibility';
import { usePopupStore } from '@/providers/PopupProvider';
import { useUserStore } from '@/providers/UserStoreProvider';
import PartialModal from '@/storyblok/PartialModal/PartialModal';
import PartialPopup from '@/storyblok/PartialPopup/PartialPopup';
import type { PartialGlobalPopupStoryblok } from '@/types/component-types-sb';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

export default function PopupManager() {
  const pathname = usePathname();
  const { popups, displayedPopups, markAsDisplayed } = usePopupStore((state) => ({
    popups: state.settings || [],
    displayedPopups: state.displayedPopups,
    markAsDisplayed: state.markAsDisplayed,
  }));

  const { loggedIn, hydrated } = useUserStore((state) => ({
    loggedIn: state.loggedIn,
    hydrated: state.hydrated,
  }));

  const [activePopup, setActivePopup] = useState<PartialGlobalPopupStoryblok | null>(null);
  const [dismissedTimeoutPopups, setDismissedTimeoutPopups] = useState<string[]>([]);
  const customPopupActiveRef = useRef(false);

  // Reset active popup immediately during the render phase on route change.
  // This avoids performance issues (cascading renders) that would occur if done inside a useEffect.
  const [prevPathname, setPrevPathname] = useState(pathname);

  if (pathname !== prevPathname) {
    setPrevPathname(pathname);
    setActivePopup(null);
    setDismissedTimeoutPopups([]);
  }

  // Handle timeout-based popups
  useEffect(() => {
    if (activePopup) return;
    customPopupActiveRef.current = false;

    // 1. Find all eligible timeout popups for the current path
    const timeoutPopups = popups.filter((p) => {
      const isActive = p.is_active;
      const isTimeout = p.trigger_event === 'timeout_event';
      const matchesPath = p.display_everywhere || isGlobalPopupPathMatch(pathname, p.target_paths);
      const wasAlreadyDisplayed = p.display_once && displayedPopups.includes(p._uid);
      const wasDismissedOnCurrentPath = dismissedTimeoutPopups.includes(p._uid);

      return isActive && isTimeout && matchesPath && !wasAlreadyDisplayed && !wasDismissedOnCurrentPath;
    });

    if (timeoutPopups.length === 0) return;

    // 2. Select the winning popup
    const timeoutPopup = selectHighestPriorityPopup(timeoutPopups);

    // Convert Storyblok timeout input (minutes) to milliseconds
    const timeoutMinutes = Number(timeoutPopup.timeout) || 0;
    const timeoutMs = timeoutMinutes * 60 * 1000;

    const timer = setTimeout(() => {
      if (customPopupActiveRef.current) return;

      setActivePopup(timeoutPopup);
      if (timeoutPopup.display_once) {
        markAsDisplayed(timeoutPopup._uid);
      }
    }, timeoutMs);

    return () => clearTimeout(timer);
  }, [activePopup, pathname, popups, displayedPopups, dismissedTimeoutPopups, markAsDisplayed]);

  // Handle custom-event-based popups (triggered via window.dispatchEvent)
  useEffect(() => {
    const customEventPopups = getEligibleCustomEventPopups(popups, pathname, displayedPopups);

    if (customEventPopups.length === 0) return;

    const popupsByEvent = new Map<string, PartialGlobalPopupStoryblok[]>();

    customEventPopups.forEach((popup) => {
      const eventName = popup.custom_event as string;
      const eventPopups = popupsByEvent.get(eventName) ?? [];
      eventPopups.push(popup);
      popupsByEvent.set(eventName, eventPopups);
    });

    // Register one listener per event. This also makes priority deterministic when
    // multiple Storyblok entries use the same custom event name.
    const handlers = new Map<string, EventListener>();

    popupsByEvent.forEach((eventPopups, eventName) => {
      const popup = selectHighestPriorityPopup(eventPopups);

      const handler = () => {
        // Re-check display status at the exact time of the event execution
        if (popup.display_once && displayedPopups.includes(popup._uid)) {
          return;
        }
        customPopupActiveRef.current = true;
        setActivePopup(popup);
        if (popup.display_once) {
          markAsDisplayed(popup._uid);
        }
      };

      handlers.set(eventName, handler);
      window.addEventListener(eventName, handler);
    });

    return () => {
      handlers.forEach((handler, eventName) => {
        window.removeEventListener(eventName, handler);
      });
    };
  }, [pathname, popups, displayedPopups, markAsDisplayed]);

  // Wait for client hydration to prevent hydration mismatch errors between server and client
  if (!activePopup || !hydrated) return null;

  const isSimulatedUser = !!process.env.NEXT_PUBLIC_SIMULATED_USER_STATE;
  const showPersonalized = loggedIn || isSimulatedUser;

  // Fallback mechanism: Attempt to show the personalized popup first,
  // fallback to the default popup if empty or user is not eligible/logged in.
  const popupToRender = (showPersonalized && activePopup.personalized_popup?.[0]) || activePopup.default_popup?.[0];

  if (!popupToRender) return null;

  const handlePopupClose = () => {
    customPopupActiveRef.current = false;
    if (activePopup.trigger_event === 'timeout_event') {
      setDismissedTimeoutPopups((dismissedPopups) =>
        dismissedPopups.includes(activePopup._uid) ? dismissedPopups : [...dismissedPopups, activePopup._uid]
      );
    }
    setActivePopup(null);
  };

  if (popupToRender.component === 'partial_modal') {
    return <PartialModal key={activePopup._uid} blok={popupToRender} onClose={handlePopupClose} />;
  }

  return <PartialPopup key={activePopup._uid} blok={popupToRender} onClose={handlePopupClose} />;
}
